import React from 'react';
import { LandTransporter } from '../../types';
import {
  XMarkIcon,
  PhoneIcon,
  MapPinIcon,
  UserIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { extractStationName } from '../../utils/stationHelpers';
import { Button } from '../ui';

interface LandTransporterDetailModalProps {
  transporter: LandTransporter | null;
  onClose: () => void;
  onEdit: (transporter: LandTransporter) => void;
  onDelete: (transporter: LandTransporter) => void;
  vehicleTypes: Array<{ id: string; name: string }>;
  canViewBusinessConstraints: boolean;
}

// ステータス色を取得する関数（スケジュール画面と完全同一）
const getStatusColor = (status: LandTransporter['status']) => {
  switch (status) {
    case 'Available':
      return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300';
    case 'Busy':
      return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300';
    case 'Unavailable':
      return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300';
    default:
      return 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-300';
  }
};

const getStatusText = (status: LandTransporter['status']) => {
  switch (status) {
    case 'Available':
      return '対応可能';
    case 'Busy':
      return '稼働中';
    case 'Unavailable':
      return '対応不可';
    default:
      return '不明';
  }
};

const LandTransporterDetailModal: React.FC<LandTransporterDetailModalProps> = ({
  transporter,
  onClose,
  onEdit,
  onDelete,
  vehicleTypes,
  canViewBusinessConstraints,
}) => {
  if (!transporter) return null;

  return (
    <div
      className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'
      onClick={onClose}
    >
      <div
        className='bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto'
        onClick={e => e.stopPropagation()}
      >
        {/* モーダルヘッダー */}
        <div className='flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700'>
          <div className='flex items-center'>
            <UserIcon className='h-6 w-6 text-primary-600 mr-3' />
            <h2 className='text-xl font-bold text-gray-800 dark:text-white'>陸送員詳細</h2>
          </div>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition'
          >
            <XMarkIcon className='h-6 w-6' />
          </button>
        </div>
        {/* モーダル内容 */}
        <div className='p-6'>
          {/* 基本情報 */}
          <div className='mb-6'>
            <div className='flex items-center justify-between mb-4'>
              <h3 className='text-lg font-bold text-gray-800 dark:text-white'>
                {transporter.name}
              </h3>
              <div
                className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(transporter.status)}`}
              >
                {getStatusText(transporter.status)}
              </div>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
              <div className='flex items-center text-gray-600 dark:text-gray-300'>
                <PhoneIcon className='h-4 w-4 mr-2' />
                <span>{transporter.phone}</span>
              </div>
              <div className='flex items-start text-gray-600 dark:text-gray-300'>
                <MapPinIcon className='h-4 w-4 mr-2 mt-0.5 flex-shrink-0' />
                <span>{transporter.address}</span>
              </div>
              {transporter.nearestStation && (
                <div className='flex items-center text-gray-600 dark:text-gray-300'>
                  <MapPinIcon className='h-4 w-4 mr-2' />
                  <span>最寄駅: {extractStationName(transporter.nearestStation)}</span>
                </div>
              )}
            </div>
          </div>
          {/* 運転可能車種 */}
          <div className='mb-6'>
            <h4 className='text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3'>
              運転可能車種
            </h4>
            <div className='flex flex-wrap gap-2'>
              {transporter.drivableVehicleTypes.map(typeId => {
                const vehicleType = vehicleTypes.find(vt => vt.id === typeId || vt.name === typeId);
                return vehicleType ? (
                  <span
                    key={typeId}
                    className='px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-sm rounded'
                  >
                    {vehicleType.name}
                  </span>
                ) : (
                  <span
                    key={typeId}
                    className='px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-sm rounded'
                  >
                    {typeId}
                  </span>
                );
              })}
            </div>
          </div>
          {/* 特記事項・資格 */}
          <div className='mb-6'>
            <h4 className='text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3'>
              特記事項・資格
            </h4>
            <div className='flex flex-wrap gap-2'>
              {transporter.hasLicenseForLargeVehicles && (
                <div className='flex items-center px-3 py-2 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 rounded-lg'>
                  <CheckCircleIcon className='h-4 w-4 mr-2' />
                  <span className='text-sm font-medium'>大型車運転免許</span>
                </div>
              )}
              {transporter.canWorkNightShift && (
                <div className='flex items-center px-3 py-2 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded-lg'>
                  <CheckCircleIcon className='h-4 w-4 mr-2' />
                  <span className='text-sm font-medium'>夜間勤務可能</span>
                </div>
              )}
              {transporter.canWorkLongDistance && (
                <div className='flex items-center px-3 py-2 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 rounded-lg'>
                  <CheckCircleIcon className='h-4 w-4 mr-2' />
                  <span className='text-sm font-medium'>長距離勤務可能</span>
                </div>
              )}
              {transporter.hasTemporaryLicense && (
                <div className='flex items-center px-3 py-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 rounded-lg'>
                  <CheckCircleIcon className='h-4 w-4 mr-2' />
                  <span className='text-sm font-medium'>仮ナンバー所有</span>
                </div>
              )}
              {transporter.isATOnly && (
                <div className='flex items-center px-3 py-2 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 rounded-lg'>
                  <CheckCircleIcon className='h-4 w-4 mr-2' />
                  <span className='text-sm font-medium'>AT限定免許</span>
                </div>
              )}
              {!transporter.hasLicenseForLargeVehicles &&
                !transporter.canWorkNightShift &&
                !transporter.canWorkLongDistance &&
                !transporter.hasTemporaryLicense &&
                !transporter.isATOnly && (
                  <span className='text-sm text-gray-500 dark:text-gray-400 italic'>
                    特記事項・資格なし
                  </span>
                )}
            </div>
          </div>

          {/* FL-180: 業務制約（権限チェック） */}
          {canViewBusinessConstraints &&
            (transporter.isSolicitationProhibited || transporter.isDesignatedStoreProhibited) && (
              <div className='mb-6'>
                <h4 className='text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3'>
                  業務制約
                </h4>
                <div className='flex flex-wrap gap-2'>
                  {transporter.isSolicitationProhibited && (
                    <div className='flex items-center px-3 py-2 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 rounded-lg'>
                      <CheckCircleIcon className='h-4 w-4 mr-2' />
                      <span
                        className='text-sm font-medium'
                        aria-label='この陸送員は客引き行為が禁止されています'
                      >
                        客引き行為NG
                      </span>
                    </div>
                  )}
                  {transporter.isDesignatedStoreProhibited && (
                    <div className='flex items-center px-3 py-2 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 rounded-lg'>
                      <CheckCircleIcon className='h-4 w-4 mr-2' />
                      <span
                        className='text-sm font-medium'
                        aria-label='この陸送員は指定店舗への配送が禁止されています'
                      >
                        指定店舗への配送NG
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* 特記事項 */}
          {transporter.notes && (
            <div className='mb-6'>
              <h4 className='text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3'>
                特記事項
              </h4>
              <div className='p-3 bg-gray-50 dark:bg-gray-700 rounded-lg'>
                <p className='text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap'>
                  {transporter.notes}
                </p>
              </div>
            </div>
          )}

          {/* システム情報 */}
          <div className='bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4'>
            <h3 className='text-sm font-medium text-gray-900 dark:text-white mb-3'>システム情報</h3>
            <div className='text-sm'>
              <div>
                <span className='text-gray-500 dark:text-gray-400'>陸送員ID: </span>
                <span className='text-gray-900 dark:text-white font-mono'>{transporter.id}</span>
              </div>
            </div>
          </div>

          {/* アクションボタン */}
          <div className='flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700'>
            <Button
              onClick={() => {
                onEdit(transporter);
                onClose();
              }}
              variant='secondary'
              size='md'
              className='flex items-center gap-2'
            >
              <PencilIcon className='h-4 w-4' />
              編集
            </Button>
            <Button
              onClick={() => {
                onDelete(transporter);
                onClose();
              }}
              variant='danger'
              size='md'
              className='flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white'
            >
              <TrashIcon className='h-4 w-4' />
              削除
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandTransporterDetailModal;
